// R58-L-08 + R58-L-09 — bulk-promote AI findings.
//
// Two TanStack-mutations against the lyse@5.8.0 endpoints:
//
//   usePromoteWithMapper  →  POST /api/lyse/v1/ai-findings/bulk-promote
//     Mapper-driven silent batch (R58-L-08). Hits become defects
//     server-side; misses come back in `needsManual` for the manual
//     batch modal to render.
//
//   usePromoteManual      →  POST /api/lyse/v1/ai-findings/bulk-promote-manual
//     Manual batch (R58-L-09). The FE pre-merges batch defaults +
//     per-row overrides and submits one row per finding.
//
// On success both invalidate the findings + defects caches for the
// inspection. Optimistic updates would race with the partial-success
// response shape (some rows promoted, others surfaced for manual
// review), so we invalidate post-success rather than optimistically
// flipping rows.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  BulkPromoteAiFindingsBody,
  BulkPromoteAiFindingsResponse,
  BulkPromoteAiFindingsManualBody,
  BulkPromoteAiFindingsManualResponse,
} from "@aeriosiq/types";
import { findingKeys } from "../../findings/queries.js";

export function usePromoteWithMapper(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  return useMutation<
    BulkPromoteAiFindingsResponse,
    Error & { errorId?: string },
    BulkPromoteAiFindingsBody
  >({
    mutationFn: async (body) => {
      const response = await api.post<BulkPromoteAiFindingsResponse>(
        "/ai-findings/bulk-promote",
        body,
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
      void qc.invalidateQueries({
        queryKey: ["defects", "list", inspectionId],
      });
    },
  });
}

export function usePromoteManual(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  return useMutation<
    BulkPromoteAiFindingsManualResponse,
    Error & { errorId?: string },
    BulkPromoteAiFindingsManualBody
  >({
    mutationFn: async (body) => {
      const response = await api.post<BulkPromoteAiFindingsManualResponse>(
        "/ai-findings/bulk-promote-manual",
        body,
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
      void qc.invalidateQueries({
        queryKey: ["defects", "list", inspectionId],
      });
    },
  });
}
